import type { Layer } from '../core/model';
import type { ToolId, VectorObjectSelection } from './types';

export type EditingContextKind = 'none' | 'image-layer' | 'raster-pixels' | 'raster-mask' | 'vector-layer' | 'vector-object' | 'path-nodes' | 'text-layer' | 'text-editing' | 'group' | 'adjustment';

export interface EditingContext {
  kind: EditingContextKind;
  family: 'none' | 'raster' | 'vector' | 'text' | 'structure';
  label: string;
  detail: string;
}

export function editingContext(
  activeLayer: Layer | undefined,
  maskEditingLayerId: string | null,
  selection: VectorObjectSelection | null,
  directTextEditingLayerId: string | null,
): EditingContext {
  if (directTextEditingLayerId && activeLayer?.kind === 'text' && activeLayer.id === directTextEditingLayerId) {
    return { kind: 'text-editing', family: 'text', label: 'Text editing', detail: activeLayer.name };
  }
  if (maskEditingLayerId && activeLayer?.kind === 'raster' && activeLayer.id === maskEditingLayerId && activeLayer.mask) {
    return { kind: 'raster-mask', family: 'raster', label: 'Raster mask', detail: activeLayer.name };
  }
  if (selection && activeLayer?.kind === 'vector' && selection.layerId === activeLayer.id) {
    if (selection.scope === 'node') return { kind: 'path-nodes', family: 'vector', label: 'Path nodes', detail: activeLayer.name };
    return {
      kind: 'vector-object',
      family: 'vector',
      label: selection.objectIds.length > 1 ? `${selection.objectIds.length} vector objects` : 'Vector object',
      detail: activeLayer.name,
    };
  }
  if (!activeLayer) return { kind: 'none', family: 'none', label: 'No editing target', detail: 'Select a layer or object' };
  if (activeLayer.kind === 'image') return { kind: 'image-layer', family: 'raster', label: 'Image layer', detail: activeLayer.name };
  if (activeLayer.kind === 'raster') return { kind: 'raster-pixels', family: 'raster', label: 'Raster pixels', detail: activeLayer.name };
  if (activeLayer.kind === 'vector') return { kind: 'vector-layer', family: 'vector', label: 'Vector layer', detail: activeLayer.name };
  if (activeLayer.kind === 'text') return { kind: 'text-layer', family: 'text', label: 'Text layer', detail: activeLayer.name };
  if (activeLayer.kind === 'group') return { kind: 'group', family: 'structure', label: 'Layer group', detail: activeLayer.name };
  return { kind: 'adjustment', family: 'structure', label: 'Adjustment layer', detail: activeLayer.name };
}

export function toolUnavailableReason(tool: ToolId, activeLayer: Layer | undefined, maskEditingLayerId: string | null): string | null {
  if (tool === 'knife') {
    if (!activeLayer) return 'Knife requires an active vector layer.';
    if (activeLayer.kind !== 'vector') return `Knife cuts filled vector artwork, not ${activeLayer.kind} layers.`;
    if (activeLayer.locked) return `${activeLayer.name} is locked.`;
    return null;
  }
  if (tool !== 'quick-selection' && tool !== 'brush' && tool !== 'history-brush' && tool !== 'clone-stamp' && tool !== 'healing-brush' && tool !== 'dodge' && tool !== 'burn' && tool !== 'eraser' && tool !== 'gradient') return null;
  const label = tool === 'quick-selection' ? 'Quick Selection' : tool === 'gradient' ? 'Gradient' : tool === 'eraser' ? 'Eraser' : tool === 'history-brush' ? 'History Brush' : tool === 'clone-stamp' ? 'Clone Stamp' : tool === 'healing-brush' ? 'Healing Brush' : tool === 'dodge' ? 'Dodge' : tool === 'burn' ? 'Burn' : 'Brush';
  if (!activeLayer) return `${label} requires an active raster layer.`;
  if (activeLayer.kind !== 'raster') return `${label} works on raster pixels${tool === 'gradient' || tool === 'quick-selection' ? '' : ' or raster masks'}, not ${activeLayer.kind} layers.`;
  if (tool === 'quick-selection') return maskEditingLayerId === activeLayer.id ? 'Quick Selection samples raster pixels, not the active mask.' : null;
  if (activeLayer.locked) return `${activeLayer.name} is locked.`;
  const editingMask = maskEditingLayerId === activeLayer.id && Boolean(activeLayer.mask);
  if (tool === 'gradient' && editingMask) return 'Gradient is unavailable while a layer mask is the editing target.';
  if (!editingMask && activeLayer.pixelsLocked) return `${activeLayer.name} has pixel locking enabled.`;
  return null;
}
